import { collabStateManager } from '@databases/collab-state-manager';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import { selectValues } from '../cellSelections/actions';
import cellSelectionsSlice from '../cellSelections/slice';
import { type AppRootState } from '../store';

let prevState: AppRootState | undefined;
let prevAnchorCell: { entryId: string; fieldId: string } | undefined;
let prevAnchorEntry: string | undefined;

/**
 * Finds the initial cell to highlight when a range selection is made.
 *
 * If the previous selection was a single cell, the anchor is the previous cell.
 * If the previous selection was a range, the anchor is the previous cell that is still in the selection.
 */
function determineAnchorCell(
  selectedValues: Array<{ entryId: string; fieldId: string }>,
  prevSelectedValues: Array<{ entryId: string; fieldId: string }>
): { entryId: string; fieldId: string } | undefined {
  // keep existing anchor if it's still in the range selection
  if (prevAnchorCell) {
    const isAnchorInSelection = selectedValues.some(
      (val) =>
        val.entryId === prevAnchorCell?.entryId && val.fieldId === prevAnchorCell?.fieldId
    );

    if (isAnchorInSelection) {
      return prevAnchorCell;
    }
  }

  // new anchor started from single cell selection
  if (prevSelectedValues.length === 1) {
    const prevCell = prevSelectedValues[0];
    if (
      selectedValues.some(
        (val) => val.entryId === prevCell.entryId && val.fieldId === prevCell.fieldId
      )
    ) {
      prevAnchorCell = prevCell;
      return prevCell;
    }
  }

  // just in case, should be unreachable
  return undefined;
}

/**
 * Finds the initial entry to highlight when a range selection is made.
 *
 * If the previous selection was a single entry, the anchor is the previous entry.
 * If the previous selection was a range, the anchor is the previous entry that is still in the selection.
 */
function determineAnchorEntry(
  selectedEntries: string[],
  prevSelectedEntries: string[]
): string | undefined {
  // keep existing anchor if it's still in the range selection
  if (prevAnchorEntry) {
    const isAnchorInSelection = selectedEntries.includes(prevAnchorEntry);

    if (isAnchorInSelection) {
      return prevAnchorEntry;
    }
  }

  // new anchor started from single entry selection
  if (prevSelectedEntries.length === 1) {
    const prevEntry = prevSelectedEntries[0];
    if (selectedEntries.includes(prevEntry)) {
      prevAnchorEntry = prevEntry;
      return prevEntry;
    }
  }

  // just in case, should be unreachable
  return undefined;
}

export const presenceMiddleware = createListenerMiddleware();

/**
 * Cell Highlighting Listener
 */
presenceMiddleware.startListening({
  actionCreator: selectValues,
  effect: (action, listenerApi) => {
    const nextState = listenerApi.getState() as AppRootState;
    const nextFocusedItem = nextState?.cellSelections?.focusedItem;

    if (nextFocusedItem?.type === 'value') {
      const prevFocusedItem = prevState?.cellSelections?.focusedItem;

      const hasPrevValueFocus = prevFocusedItem?.type === 'value';
      const hasChanged =
        !hasPrevValueFocus ||
        prevFocusedItem.entryId !== nextFocusedItem.entryId ||
        prevFocusedItem.fieldId !== nextFocusedItem.fieldId;

      if (hasChanged) {
        const collabManager = collabStateManager.getCollabManager(
          action.payload.contentId
        );

        if (collabManager) {
          const selectedValues = nextState?.cellSelections?.selectedValues || [];
          const isRangeSelection = selectedValues.length > 1;

          let targetEntryId: string;
          let targetFieldId: string;

          if (isRangeSelection) {
            const prevSelectedValues = prevState?.cellSelections?.selectedValues || [];
            const anchorCell = determineAnchorCell(selectedValues, prevSelectedValues);

            if (anchorCell) {
              targetEntryId = anchorCell.entryId;
              targetFieldId = anchorCell.fieldId;
            } else {
              // just in case, should be unreachable
              prevAnchorCell = undefined;
              targetEntryId = nextFocusedItem.entryId;
              targetFieldId = nextFocusedItem.fieldId;
            }
          } else {
            // single cell, reset anchor
            prevAnchorCell = undefined;
            targetEntryId = nextFocusedItem.entryId;
            targetFieldId = nextFocusedItem.fieldId;
          }

          collabManager.updateTelepointer(targetEntryId, targetFieldId);
        }
      }
    }

    prevState = nextState;
  },
});

/**
 * Entry/Row Select Highlighting Listener
 */
presenceMiddleware.startListening({
  matcher: (action) => {
    return (
      cellSelectionsSlice.actions.selectEntries.match(action) ||
      cellSelectionsSlice.actions.deselectEntries.match(action)
    );
  },
  effect: (action, listenerApi) => {
    const nextState = listenerApi.getState() as AppRootState;
    const selectedEntries = nextState.cellSelections.selectedEntireEntries;

    const collabManager = collabStateManager.getCollabManager(action.payload.contentId);
    const database = selectDatabaseByContentIdOptional(nextState, {
      contentId: action.payload.contentId,
    });

    if (selectedEntries.length > 0 && database && !isBrokenDatabase(database)) {
      const isRangeSelection = selectedEntries.length > 1;

      let targetEntryId: string | undefined;

      if (isRangeSelection) {
        const prevSelectedEntries =
          prevState?.cellSelections?.selectedEntireEntries || [];
        const anchorEntry = determineAnchorEntry(selectedEntries, prevSelectedEntries);

        if (anchorEntry) {
          targetEntryId = anchorEntry;
        } else {
          // just in case, should be unreachable
          prevAnchorEntry = undefined;
          targetEntryId = selectedEntries[0];
        }
      } else {
        // single row, reset anchor
        prevAnchorEntry = undefined;
        targetEntryId = selectedEntries[0];
      }

      if (targetEntryId && collabManager) {
        collabManager.updateTelepointer(targetEntryId, undefined);
      }
    } else {
      prevAnchorEntry = undefined;
      if (collabManager) {
        collabManager.updateTelepointer(undefined, undefined);
      }
    }

    prevState = nextState;
  },
});

/**
 * Field/Column Select Highlighting Listener
 */
presenceMiddleware.startListening({
  actionCreator: cellSelectionsSlice.actions.selectFields,
  effect: (action) => {
    const selectedFieldId = action.payload.focusFieldId;

    const collabManager = collabStateManager.getCollabManager(action.payload.contentId);

    if (collabManager && selectedFieldId) {
      collabManager.updateTelepointer(undefined, selectedFieldId);
    }
  },
});
